import React from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import type { ChatMessage } from '@cubemeet/core';
import { shouldShowDateSeparator } from '@cubemeet/core';
import { ChatMessageBubble } from './ChatMessageBubble';
import { ChatDateSeparator } from './ChatDateSeparator';
import { ChatEmptyState } from './ChatEmptyState';

export interface ChatMessageListProps {
  /** 消息列表 */
  messages: ChatMessage[];
  /** 当前用户 ID */
  currentUserId?: string;
  /** 主题颜色 */
  themeColor?: string;
  /** 是否正在加载 */
  loading?: boolean;
  /** 格式化时间的函数 */
  formatTime: (dateString: string) => string;
  /** 格式化日期的函数 */
  formatDate: (dateString: string) => string;
  /** 获取发送者名称的函数 */
  getSenderName: (message: ChatMessage) => string;
  /** 空状态配置 */
  emptyState?: {
    icon?: string;
    title?: string;
    subtitle?: string;
  };
  /** 加载状态文本 */
  loadingText?: string;
  /** 容器样式 */
  containerStyle?: ViewStyle;
  /** 内容容器样式 */
  contentContainerStyle?: ViewStyle;
  /** ScrollView ref (use any to avoid cross-package react-native type mismatches) */
  scrollRef?: React.RefObject<any>;
  /** 内容尺寸变化回调 */
  onContentSizeChange?: () => void;
}

/**
 * 聊天消息列表组件
 * 
 * 显示消息列表，包含日期分隔符、消息气泡、空状态等
 * 
 * @example
 * ```tsx
 * <ChatMessageList
 *   messages={messages}
 *   currentUserId={user.id}
 *   themeColor="#4A90E2"
 *   formatTime={(date) => formatMessageTime(date)}
 *   formatDate={(date) => formatMessageDate(date)}
 *   getSenderName={(msg) => getMessageSenderName(msg)}
 * />
 * ```
 */
export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  currentUserId,
  themeColor = '#4A90E2',
  loading = false,
  formatTime,
  formatDate,
  getSenderName,
  emptyState,
  loadingText = '加载中...',
  containerStyle,
  contentContainerStyle,
  scrollRef,
  onContentSizeChange,
}) => {
  // 渲染单条消息
  const renderMessage = (message: ChatMessage, index: number) => {
    const isOwn = message.user_id === currentUserId;
    const previousMessage = index > 0 ? messages[index - 1] : null;
    const showDate = shouldShowDateSeparator(message, previousMessage);

    return (
      <View key={message.id}>
        {/* 日期分隔符 */}
        {showDate && (
          <ChatDateSeparator date={formatDate(message.created_at)} />
        )}

        {/* 消息气泡 */}
        <ChatMessageBubble
          message={message.message}
          time={formatTime(message.created_at)}
          isOwn={isOwn}
          senderName={!isOwn ? getSenderName(message) : undefined}
          themeColor={themeColor}
        />
      </View>
    );
  };

  return (
    <ScrollView
      ref={scrollRef}
      style={[styles.container, containerStyle]}
      contentContainerStyle={[styles.content, contentContainerStyle]}
      showsVerticalScrollIndicator={false}
      onContentSizeChange={onContentSizeChange}
    >
      {loading && messages.length === 0 ? (
        <ChatEmptyState
          icon="hourglass-empty"
          title={loadingText}
        />
      ) : messages.length === 0 ? (
        <ChatEmptyState
          icon={emptyState?.icon || 'chat-bubble-outline'}
          title={emptyState?.title || '暂无消息'}
          subtitle={emptyState?.subtitle || '发送第一条消息开始聊天'}
        />
      ) : (
        messages.map((message, index) => renderMessage(message, index))
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
});
